import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';
import { contactData } from '../data/mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    time: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock form submission
    console.log('Form submitted:', formData);
    
    // Show success toast
    toast.success('Appointment request sent! We\'ll contact you within 24 hours to confirm your booking.');
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      date: '',
      time: '',
      message: ''
    });
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section" style={{ background: 'var(--warm-gradient)' }}>
        <div className="container text-center">
          <h1 className="mb-6 fade-in" style={{ color: 'var(--charcoal)' }}>
            Book Your Appointment
          </h1>
          <p className="text-xl max-w-3xl mx-auto fade-in-delay-1" style={{ color: 'var(--charcoal)' }}>
            Your transformation starts here. Contact us to schedule your personalized beauty consultation 
            and discover the luxury experience that awaits you.
          </p>
        </div>
      </section>

      <div className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="fade-in">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="mb-6" style={{ color: 'var(--charcoal)' }}>
                  Schedule Your Visit
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                        placeholder="Enter your full name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                        placeholder="Enter your phone number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Preferred Service
                      </label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                      >
                        <option value="">Select a service</option>
                        <option value="facial">Facials & Skincare</option>
                        <option value="injectables">Injectables & Anti-Aging</option>
                        <option value="hair">Luxury Hair & Styling</option>
                        <option value="body">Body Treatments</option>
                        <option value="consultation">Consultation Only</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Preferred Date
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                        Preferred Time
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors"
                        style={{ 
                          borderColor: 'var(--warm-beige)',
                          focusRingColor: 'var(--champagne-gold)'
                        }}
                      >
                        <option value="">Select time</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--charcoal)' }}>
                      Additional Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors resize-none"
                      style={{ 
                        borderColor: 'var(--warm-beige)',
                        focusRingColor: 'var(--champagne-gold)'
                      }}
                      placeholder="Tell us about your beauty goals, any special requests, or questions you may have..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center space-x-2"
                  >
                    <Send size={20} />
                    <span>Send Appointment Request</span>
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-8 fade-in-delay-1">
              {/* Contact Details */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="mb-6" style={{ color: 'var(--charcoal)' }}>
                  Get In Touch
                </h3>
                
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--charcoal)' }}>
                        Visit Our Salon
                      </h4>
                      <p className="text-gray-600">{contactData.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--charcoal)' }}>
                        Call Us
                      </h4>
                      <p className="text-gray-600">
                        <a href={`tel:${contactData.phone}`} className="hover:underline">
                          {contactData.phone}
                        </a>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1" style={{ color: 'var(--charcoal)' }}>
                        Email Us
                      </h4>
                      <p className="text-gray-600">
                        <a href={`mailto:${contactData.email}`} className="hover:underline">
                          {contactData.email}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--champagne-gold)' }}>
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <h3 style={{ color: 'var(--charcoal)' }}>Opening Hours</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--charcoal)' }}>Monday - Friday</span>
                    <span className="text-gray-600">9:00 AM - 7:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--charcoal)' }}>Saturday</span>
                    <span className="text-gray-600">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--charcoal)' }}>Sunday</span>
                    <span className="text-gray-600">10:00 AM - 5:00 PM</span>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="h-64 bg-gray-200 flex items-center justify-center">
                  <iframe
                    src={contactData.mapUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Luxe Beauty Salon Location"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <section className="section bg-charcoal text-white" style={{ backgroundColor: 'var(--charcoal)' }}>
        <div className="container text-center">
          <h2 className="mb-6 text-white fade-in">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto fade-in-delay-1">
            Don't wait to look and feel your best. Our expert team is ready to create 
            a personalized beauty experience just for you.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 fade-in-delay-2">
            <a href={`tel:${contactData.phone}`} className="btn-primary">
              Call Now
            </a>
            <a href={`mailto:${contactData.email}`} className="btn-secondary">
              Email Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;